import type { QueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { getSession, refresh } from "@/api/auth";
import { newspapersQuery } from "@/api/queries";
import { EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { InvitePage, LoginPage, ResetPage } from "@/features/auth/AuthPages";
import { Shell } from "@/features/shell/Shell";
import { lastNewspaper } from "@/lib/session";

type Ctx = { queryClient: QueryClient };

const rootRoute = createRootRouteWithContext<Ctx>()({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
});

function NoNewspaper() {
  const admin = getSession()?.user.role === "admin";
  return (
    <EmptyState
      title={admin ? "Add your first newspaper" : "You're not on any newspaper yet"}
      className="h-full"
      action={
        admin && (
          <Button asChild variant="primary">
            <Link to="/admin/newspapers" search={{ new: true }}>
              Add a newspaper
            </Link>
          </Button>
        )
      }
    >
      {admin
        ? "Each newspaper has its own logo, colours, templates and reels. Then invite the people who'll make reels."
        : "Ask an admin to add you to a newspaper. You'll see its reels here."}
    </EmptyState>
  );
}

function NotFound() {
  return (
    <EmptyState title="Page not found" className="h-full">
      <p>That address doesn't lead anywhere.</p>
      <Button asChild variant="secondary" className="mt-3">
        <Link to="/">Go home</Link>
      </Button>
    </EmptyState>
  );
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: z.object({ next: z.string().optional() }),
  component: LoginPage,
});

const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: "/invite", component: InvitePage });
const resetRoute = createRoute({ getParentRoute: () => rootRoute, path: "/reset", component: ResetPage });

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ location }) => {
    if (!getSession() && !(await refresh())) {
      throw redirect({ to: "/login", search: { next: location.href } });
    }
  },
  component: Shell,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: async ({ context }) => {
    const papers = await context.queryClient.ensureQueryData(newspapersQuery);
    const last = lastNewspaper();
    const np = papers.find((p) => p.id === last) ?? papers[0];
    if (np) throw redirect({ to: "/n/$npId/reels", params: { npId: np.id } });
  },
  component: NoNewspaper,
});

const range = z.enum(["today", "yesterday", "7d", "30d", "all"]);
export const reelsSearch = z.object({
  view: z.enum(["board", "table"]).catch("board").optional(),
  range: range.catch("7d").optional(),
  q: z.string().catch("").optional(),
  tpl: z.string().optional(),
  status: z.enum(["draft", "building", "review", "published", "attention"]).optional().catch(undefined),
  new: z.boolean().optional().catch(undefined),
});

const reelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/n/$npId/reels",
  validateSearch: reelsSearch,
  component: lazyRouteComponent(() => import("@/features/board/ReelsPage"), "ReelsPage"),
});

const trashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/n/$npId/trash",
  component: lazyRouteComponent(() => import("@/features/board/TrashPage"), "TrashPage"),
});

const templatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/n/$npId/templates",
  component: lazyRouteComponent(() => import("@/features/templates/TemplatesPage"), "TemplatesPage"),
});

const templateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/n/$npId/templates/$tplId",
  component: lazyRouteComponent(() => import("@/features/templates/TemplateEditor"), "TemplateEditorPage"),
});

const peopleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/n/$npId/people",
  component: lazyRouteComponent(() => import("@/features/people/PeoplePage"), "PeoplePage"),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/n/$npId/settings/$tab",
  component: lazyRouteComponent(() => import("@/features/settings/SettingsPage"), "SettingsPage"),
});

export const reelSearch = z.object({
  stage: z.enum(["edit", "build", "review"]).optional().catch(undefined),
  card: z.string().optional(),
});

const reelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reels/$reelId",
  validateSearch: reelSearch,
  component: lazyRouteComponent(() => import("@/features/editor/ReelWorkspace"), "ReelWorkspace"),
});

const membersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/members",
  validateSearch: z.object({ tab: z.enum(["people", "invites"]).optional().catch(undefined) }),
  component: lazyRouteComponent(() => import("@/features/admin/MembersPage"), "MembersPage"),
});

const adminNewspapersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin/newspapers",
  validateSearch: z.object({ new: z.boolean().optional().catch(undefined) }),
  component: lazyRouteComponent(() => import("@/features/admin/NewspapersAdminPage"), "NewspapersAdminPage"),
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/account",
  component: lazyRouteComponent(() => import("@/features/account/AccountPage"), "AccountPage"),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  resetRoute,
  appRoute.addChildren([
    indexRoute,
    reelsRoute,
    trashRoute,
    templatesRoute,
    templateRoute,
    settingsRoute,
    peopleRoute,
    reelRoute,
    membersRoute,
    adminNewspapersRoute,
    accountRoute,
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
